import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { 
  Shield, 
  Heart, 
  Users, 
  MessageCircle, 
  BarChart3, 
  CheckCircle,
  ArrowRight,
  Star,
  Lock,
  Play,
  ChevronDown,
  Sparkles,
  UserCheck,
  Brain,
  Clock,
  Activity,
  Smile,
  Globe,
  Eye,
  Headphones,
  CreditCard
} from 'lucide-react'

import { Navigation } from '../components/Navigation'
import { Button } from '../components/ui/Button'
import { products } from '../lib/stripe-config'

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
}

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
}

export const Landing: React.FC = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [howItWorksRef, howItWorksInView] = useInView({ threshold: 0.1, triggerOnce: true })
  const [pricingRef, pricingInView] = useInView({ threshold: 0.1, triggerOnce: true })

  const features = [
    {
      icon: Heart,
      title: "Gentle Check-ins",
      description: "Weekly wellness conversations that feel natural and supportive, never invasive or clinical",
      color: "from-sage-400 to-sage-600"
    },
    {
      icon: Shield,
      title: "Complete Privacy",
      description: "Your mental health data is protected with military-grade encryption and HIPAA compliance",
      color: "from-blue-400 to-blue-600"
    },
    {
      icon: Brain,
      title: "Empathetic AI",
      description: "AI that understands emotional nuances with the sensitivity of a trained therapist",
      color: "from-lavender-400 to-lavender-600"
    },
    {
      icon: Activity,
      title: "Real-time Insights",
      description: "Understand your team's emotional wellbeing as it happens, not weeks later",
      color: "from-emerald-400 to-emerald-600"
    },
    {
      icon: Users,
      title: "Team Wellness",
      description: "Foster a culture of openness and support across your entire organization",
      color: "from-sage-500 to-emerald-500"
    },
    {
      icon: Sparkles,
      title: "Positive Growth",
      description: "Track wellness improvements and celebrate your team's mental health journey",
      color: "from-blue-400 to-lavender-500"
    }
  ]

  const steps = [
    {
      number: "01",
      title: "Simple Setup",
      description: "Create your wellness space in minutes with our guided, stress-free onboarding process",
      icon: UserCheck,
      color: "from-sage-400 to-sage-600"
    },
    {
      number: "02",
      title: "Comfortable Check-ins",
      description: "Team members share how they're feeling through voice or text - whatever feels right",
      icon: MessageCircle,
      color: "from-blue-400 to-blue-600"
    },
    {
      number: "03",
      title: "Caring Analysis",
      description: "Our AI processes responses with empathy and understanding, never judgment",
      icon: Brain,
      color: "from-lavender-400 to-lavender-600"
    },
    {
      number: "04",
      title: "Supportive Action",
      description: "Receive gentle insights and recommendations to support your team's wellbeing",
      icon: Sparkles,
      color: "from-emerald-400 to-emerald-600"
    }
  ]

  const testimonials = [
    {
      name: "Dr. Sarah Chen",
      role: "Chief People Officer at TechFlow",
      content: "Mentra created a safe space where our team feels comfortable sharing. The insights help us provide better support.",
      rating: 5,
      avatar: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop"
    },
    {
      name: "Marcus Rodriguez",
      role: "Engineering Director at InnovateLabs",
      content: "Finally, a mental health platform that doesn't feel clinical. Our team engagement and happiness have improved significantly.",
      rating: 5,
      avatar: "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop"
    },
    {
      name: "Aisha Patel",
      role: "Product Manager at HealthTech Solutions",
      content: "The AI feels genuinely caring. It's like having a wellness coach who truly understands our team's unique needs.",
      rating: 5,
      avatar: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop"
    }
  ]

  return (
    <div className="min-h-screen bg-calming">
      <Navigation variant="landing" />

      {/* Bolt.new Badge */}
<div className="fixed top-4 right-4 z-50">
  <a href="https://bolt.new/" target="_blank" rel="noopener noreferrer">
    <img 
      src="/white_circle_360x360.png" 
      alt="Built with Bolt.new" 
      className="w-12 h-12 hover:scale-110 transition-transform duration-300"
    />
  </a>
</div>


      {/* Hero Section - Clean and Calming */}
      <section ref={heroRef} className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="initial"
            animate={heroInView ? "animate" : "initial"}
            variants={staggerContainer}
            className="text-center"
          >
            {/* Trust Badge */}
            <motion.div variants={fadeInUp} className="mb-8">
              <div className="inline-flex items-center px-6 py-3 bg-white/90 backdrop-blur-sm rounded-full text-sage-700 text-sm font-medium border border-sage-200 shadow-sm">
                <div className="w-2 h-2 bg-sage-500 rounded-full mr-3 animate-pulse" />
                Trusted by 1,000+ teams worldwide
                <Shield className="w-4 h-4 ml-3 text-sage-600" />
              </div>
            </motion.div>
            
            {/* Main Headline - Clear and Direct */}
            <motion.div variants={fadeInUp} className="mb-12">
              <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Mental Wellness Made
                <span className="block text-sage-600">Simple & Safe</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
                Create a supportive workplace where your team's mental health thrives through 
                gentle AI insights and empathetic care.
              </p>
            </motion.div>

            {/* FIXED: Clear CTAs - No dashboard button, only Sign In and Register */}
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/auth/register">
                <Button size="lg" className="text-lg px-8 py-4 bg-sage-600 hover:bg-sage-700 text-white">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button variant="outline" size="lg" className="text-lg px-8 py-4 border-slate-300 text-slate-700 hover:bg-slate-50">
                <Play className="w-5 h-5 mr-2" />
                Watch Demo
              </Button>
            </motion.div>

            {/* High-Quality Mental Health Image */}
            <motion.div variants={fadeInUp} className="relative max-w-4xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=1200&h=600&fit=crop"
                  alt="Team wellness and mental health support"
                  className="w-full h-80 object-cover"
                />
                
                {/* Subtle overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-sage-600/10 to-lavender-600/10" />
                
                {/* Floating wellness indicators */}
                <motion.div
                  className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-sage-500 rounded-full animate-pulse" />
                    <span className="text-sm font-medium text-slate-800">Team Wellness: 92%</span>
                  </div>
                </motion.div>

                <motion.div
                  className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg"
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                >
                  <div className="flex items-center space-x-2">
                    <Heart className="w-4 h-4 text-sage-500" />
                    <span className="text-sm font-medium text-slate-800">AI Care Active</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-6 h-6 text-slate-400" />
        </motion.div>
      </section>

      {/* Features Section - Clean Grid Layout */}
      <section id="features" ref={featuresRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate={featuresInView ? "animate" : "initial"}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Built for Your Team's Wellbeing
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Every feature is designed with mental health best practices in mind
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial="initial"
            animate={featuresInView ? "animate" : "initial"}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div 
                key={feature.title} 
                variants={fadeInUp}
                className="bg-white rounded-2xl p-8 text-center border border-slate-200 hover:border-slate-300 transition-all duration-300 hover:shadow-lg gentle-hover"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" ref={howItWorksRef} className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate={howItWorksInView ? "animate" : "initial"}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Simple Steps to Better Wellness
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Getting started takes just minutes, not hours
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial="initial"
            animate={howItWorksInView ? "animate" : "initial"}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {steps.map((step, index) => (
              <motion.div key={step.number} variants={fadeInUp}>
                <div className="text-center">
                  <div className="relative mb-8">
                    <div className={`w-20 h-20 bg-gradient-to-r ${step.color} rounded-full flex items-center justify-center mx-auto mb-4 gentle-hover`}>
                      <step.icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white border-2 border-sage-500 text-sage-600 rounded-full flex items-center justify-center text-sm font-bold">
                      {step.number}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Trusted by Caring Leaders
            </h2>
            <p className="text-xl text-slate-600">
              Real stories from teams who've transformed their workplace wellness
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white rounded-2xl p-8 text-center border border-slate-200 shadow-sm gentle-hover"
              >
                <div className="flex justify-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-current" />
                  ))}
                </div>
                <p className="text-slate-600 mb-6 italic text-lg leading-relaxed">"{testimonial.content}"</p>
                <div className="flex items-center justify-center">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mr-4 object-cover"
                  />
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" ref={pricingRef} className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            animate={pricingInView ? "animate" : "initial"}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Choose the perfect plan for your team's wellness journey
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial="initial"
            animate={pricingInView ? "animate" : "initial"}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          >
            {products.map((plan, index) => (
              <motion.div key={plan.id} variants={fadeInUp}>
                <div className={`bg-white rounded-2xl p-8 text-center border-2 transition-all duration-300 relative gentle-hover ${
                  plan.name === 'Professional' 
                    ? 'border-sage-500 shadow-lg' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}>
                  {plan.name === 'Professional' && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-sage-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                        Most Popular
                      </div>
                    </div>
                  )}
                  
                  <div className={`w-16 h-16 bg-gradient-to-r ${
                    plan.name === 'Professional' ? 'from-blue-400 to-blue-600' : 'from-sage-400 to-sage-600'
                  } rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    {plan.name === 'Professional' ? (
                      <Brain className="w-8 h-8 text-white" />
                    ) : (
                      <Heart className="w-8 h-8 text-white" />
                    )}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-slate-900">${plan.price}</span>
                    <span className="text-slate-500 ml-2">per user/month</span>
                  </div>
                  <p className="text-slate-600 mb-8">{plan.description}</p>
                  
                  <ul className="space-y-3 mb-8 text-left">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-sage-500 mr-3 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/auth/register">
                    <Button 
                      variant={plan.name === 'Professional' ? "primary" : "outline"} 
                      className={`w-full ${plan.name === 'Professional' ? 'bg-sage-600 hover:bg-sage-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                      size="lg"
                    >
                      Start Free Trial
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Guarantee */}
          <div className="text-center mt-12">
            <div className="inline-flex items-center px-6 py-3 bg-sage-100 rounded-full text-sage-700 font-medium">
              <Shield className="w-5 h-5 mr-2" />
              14-day free trial • No setup fees • Cancel anytime
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - FIXED: No dashboard button */}
      <section className="py-20 bg-sage-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Support Your Team's Wellness?
            </h2>
            <p className="text-xl text-sage-100 mb-8 max-w-2xl mx-auto">
              Join thousands of teams creating healthier, happier workplaces with Mentra
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/auth/register">
                <Button 
                  size="lg" 
                  className="text-lg px-8 py-4 bg-white text-sage-700 hover:bg-gray-50"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                className="text-lg px-8 py-4 bg-sage-700 text-white border border-sage-500 hover:bg-sage-800"
              >
                <Clock className="w-5 h-5 mr-2" />
                Schedule Demo
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sage-100">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>Free 14-day trial</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>Setup in 5 minutes</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-sage-600 rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold">Mentra</span>
              </div>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Creating healthier workplaces through AI-powered mental wellness insights and empathetic care.
              </p>
              <div className="flex space-x-4">
                {[Globe, Heart, Users, Shield].map((Icon, index) => (
                  <div
                    key={index}
                    className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center cursor-pointer hover:bg-slate-700 transition-colors"
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-6 text-lg">Product</h4>
              <ul className="space-y-3 text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-6 text-lg">Company</h4>
              <ul className="space-y-3 text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-6 text-lg">Support</h4>
              <ul className="space-y-3 text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">HIPAA Compliance</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-400">
            <p>&copy; 2025 Mentra. All rights reserved. Built with care for workplace wellness.</p>
            <div className="mt-2 flex items-center justify-center">
              <span className="text-sm mr-2">Powered by</span>
              <a href="https://bolt.new/" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://raw.githubusercontent.com/kickiniteasy/bolt-hackathon-badge/main/text-only.svg" 
                  alt="Built with Bolt.new" 
                  className="h-4"
                />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
